import { useId, type KeyboardEvent, type ReactNode } from 'react';

/**
 * One choice from a few, shown all at once: List / Map, Light / Dark / System.
 *
 * A radio group underneath, so arrow keys move the choice and a screen reader
 * announces "2 of 3" - the same keyboard model as Linear's view switchers.
 * Use `<Tabs>` when each option swaps a whole panel and `<Select>` past about
 * five options; this is for the small, always-visible switch between them.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  /** Accessible name when `label` is an icon alone. */
  ariaLabel?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  size?: 'sm' | 'md';
  /** Stretches the segments to fill the row - for menus and narrow panels. */
  block?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  block = false,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const name = useId();

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;

    if (!step) {
      return;
    }

    event.preventDefault();
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + step + options.length) % options.length];
    onChange(next.value);
    (event.currentTarget.querySelector(`[data-value="${next.value}"]`) as HTMLElement | null)?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={rest['aria-label']}
      className={['cw-segmented', `cw-segmented--${size}`, block && 'cw-segmented--block', className]
        .filter(Boolean)
        .join(' ')}
      onKeyDown={onKeyDown}
    >
      {options.map((option) => {
        const checked = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            name={name}
            aria-checked={checked}
            aria-label={option.ariaLabel}
            tabIndex={checked ? 0 : -1}
            data-value={option.value}
            className={['cw-segmented__item', checked && 'is-checked'].filter(Boolean).join(' ')}
            onClick={() => onChange(option.value)}
          >
            {option.icon}
            {option.label && <span className="cw-segmented__label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;

export interface ToggleGroupProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  'aria-label': string;
  size?: 'sm' | 'md';
  block?: boolean;
  className?: string;
  disabled?: boolean;
}

/**
 * The segmented look for a choice of several - e.g. which bases of value are
 * asked for. Each segment is a pressed/unpressed toggle; order follows
 * `options`, not the order clicked.
 */
export function ToggleGroup<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  block = false,
  className,
  disabled,
  ...rest
}: ToggleGroupProps<T>) {
  const toggle = (item: T) =>
    onChange(
      options
        .map((option) => option.value)
        .filter((v) => (v === item ? !value.includes(v) : value.includes(v)))
    );

  return (
    <div
      role="group"
      aria-label={rest['aria-label']}
      className={['cw-segmented', `cw-segmented--${size}`, block && 'cw-segmented--block', className]
        .filter(Boolean)
        .join(' ')}
    >
      {options.map((option) => {
        const pressed = value.includes(option.value);

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            aria-label={option.ariaLabel}
            disabled={disabled}
            className={['cw-segmented__item', pressed && 'is-checked'].filter(Boolean).join(' ')}
            onClick={() => toggle(option.value)}
          >
            {option.icon}
            {option.label && <span className="cw-segmented__label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
